import styled from "styled-components";
import CardFrame from "./CardFrame";
import { useQuery } from "@tanstack/react-query";
import { getPostStreak } from "api";
import { connect } from "react-redux";
import moment from "moment";
import { useEffect } from "react";

const TrackerContainer = styled.div`
  color: #333;
  margin: 0 auto;
  text-align: center;
  background: #fff;
  padding: 8px;
  width: 100%;
  display: flex;
  gap: 8px;
  overflow: hidden;
  width: 100%;
  height: fit-content;
  overflow: visible;
`;

const WeekdayLabels = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;

  span {
    font-size: 10px;
    color: #808080;
    width: 30px;
    height: 13.53px;
    display: flex;
    align-items: center;
  }
`;

const GridContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const WeeksContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 4px;
  padding-bottom: -10px;
`;

const MonthLabelsContainer = styled.div`
  display: flex;
  gap: 4px;
  margin-top: 4px;
  position: relative;
  z-index: 1;
  height: 20px;
`;

const MonthLabel = styled.p`
  font-size: 12px;
  color: #808080;
  position: absolute;
  left: 0;
  transform: translateX(-50%);
  white-space: nowrap;
`;

const WeekColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Day = styled.div`
  width: 13.53px;
  height: 13.53px;
  border-radius: 3px;
  background-color: #f2f2f2;
  position: relative;

  &:not(.preview):hover::before {
    content: attr(data-tooltip);
    position: absolute;
    top: -30px;
    left: 50%;
    transform: translateX(-50%);
    background-color: #333;
    color: #fff;
    font-size: 10px;
    padding: 4px 8px;
    border-radius: 4px;
    white-space: nowrap;
    z-index: 5;
  }

  &:hover::after {
    content: "";
    position: absolute;
    top: -8px;
    left: 50%;
    transform: translateX(-50%);
    border-width: 4px;
    border-style: solid;
    border-color: #333 transparent transparent transparent;
    z-index: 5;
  }

  &.level1 {
    background-color: #ebf6ff;
  }
  &.level2 {
    background-color: #72c4ff;
  }
  &.level3 {
    background-color: #149dff;
  }
  &.level4 {
    background-color: #128de6;
  }
`;

const getDaysInMonth = (month, year) => {
  return new Date(year, month + 1, 0).getDate();
};

const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const PostStreakTracker = ({ brand }) => {
  const socials = brand?.socials?.socials ?? [];
  const startDate = new Date(moment().subtract(3, "months").format());
  const endDate = new Date(moment().format());

  const { data: postStreakData } = useQuery({
    queryKey: ["postStreakData", socials[0]?.platform],
    queryFn: () =>
      getPostStreak(socials[0]?.platform, {
        start_date: startDate,
        end_date: endDate,
      }),
    select: (data) => data?.data?.data,
    enabled: !!socials?.length,
  });

  const getWeekday = (date) => date.getDay();
  const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  useEffect(() => {
    if (postStreakData?.post_tracker) {
      const formattedData = [];
      const startDate = new Date(moment().subtract(3, "months").format());
      const endDate = new Date(moment().format());
      const year = startDate.getFullYear();

      for (
        let month = startDate.getMonth();
        month <= endDate.getMonth();
        month++
      ) {
        const daysInMonth = getDaysInMonth(month, year);

        for (let day = 1; day <= daysInMonth; day++) {
          const dateStr = new Date(year, month, day)
            .toISOString()
            .split("T")[0];
          formattedData.push({
            date: new Date(dateStr),
            posts: postStreakData.post_tracker[dateStr] || 0,
          });
        }
      }
    }
  }, [postStreakData]);

  const groupIntoWeeks = (days) => {
    const weeks = [];
    let currentWeek = [];

    days.forEach((day) => {
      currentWeek.push(day);
      if (currentWeek.length === 7) {
        weeks.push(currentWeek);
        currentWeek = [];
      }
    });

    if (currentWeek.length > 0) {
      weeks.push(currentWeek);
    }

    return weeks;
  };

  return (
    <CardFrame
      headerText="Post streak tracker"
      bg="#EBF6FF"
      border="1px solid #149DFF"
      className="flexColumn justifySpaceBetween"
    >
      <div className="fullWidth flexColumn justifyEnd">
        <TrackerContainer>
          <WeekdayLabels>
            {WEEKDAYS.map((day) => (
              <span key={day}>{day}</span>
            ))}
          </WeekdayLabels>

          <GridContainer>
            {(() => {
              let currentDate = new Date(startDate);
              let allDays = [];

              // Calculate initial empty days
              const firstDayOfWeek = getWeekday(currentDate);
              for (let i = 0; i < firstDayOfWeek; i++) {
                allDays.push({ isEmpty: true });
              }

              while (currentDate <= endDate) {
                const isInRange =
                  currentDate >= startDate && currentDate <= endDate;
                const dateStr = currentDate.toISOString().split("T")[0];

                allDays.push({
                  date: new Date(currentDate),
                  posts: isInRange
                    ? postStreakData?.post_tracker?.[dateStr] || 0
                    : null,
                  isEmpty: false,
                });

                currentDate.setDate(currentDate.getDate() + 1);
              }

              const weeks = groupIntoWeeks(allDays);

              return (
                <>
                  <WeeksContainer>
                    {weeks.map((week, weekIndex) => (
                      <WeekColumn key={weekIndex}>
                        {week.map((day, dayIndex) => (
                          <Day
                            key={dayIndex}
                            className={
                              day.isEmpty ? "" : `level${day.posts || 0}`
                            }
                            data-tooltip={
                              !day.isEmpty && day.date
                                ? `${day.date.toDateString()}: ${
                                    day.posts || 0
                                  } posts`
                                : ""
                            }
                          />
                        ))}
                      </WeekColumn>
                    ))}
                  </WeeksContainer>

                  <MonthLabelsContainer>
                    {(() => {
                      const months = new Set();
                      const monthLabels = [];

                      allDays.forEach((day, index) => {
                        if (!day.isEmpty && day.date) {
                          const monthKey = `${day.date.getFullYear()}-${day.date.getMonth()}`;
                          if (!months.has(monthKey)) {
                            months.add(monthKey);
                            // Calculate position: (column index * (box width + gap))
                            const position =
                              Math.floor(index / 7) * (13.53 + 4);
                            monthLabels.push({
                              month: MONTH_NAMES[day.date.getMonth()],
                              position,
                            });
                          }
                        }
                      });

                      return monthLabels.map((label, index) => (
                        <MonthLabel
                          key={index}
                          style={{
                            left: `${label.position}px`,
                          }}
                        >
                          {label.month}
                        </MonthLabel>
                      ));
                    })()}
                  </MonthLabelsContainer>
                </>
              );
            })()}
          </GridContainer>
        </TrackerContainer>
      </div>
    </CardFrame>
  );
};

const mapStateToProps = (state) => {
  return {
    brand: state.brand,
  };
};

export default connect(mapStateToProps)(PostStreakTracker);
